// src/json-keywords.ts
import { syntaxTree } from "@codemirror/language";
import { RangeSetBuilder, Extension } from "@codemirror/state";
import {
  ViewPlugin,
  Decoration,
  DecorationSet,
  ViewUpdate,
  EditorView,
  PluginValue,
} from "@codemirror/view";
import { SyntaxNodeRef } from "@lezer/common";

const keywordDecoration = Decoration.mark({ class: "tok-json-schema-type" });
const atomDecoration = Decoration.mark({ class: "tok-atom" });

/**
 * A function that iterates through the syntax tree and creates decorations
 * for our specified keywords.
 */
const createKeywordDecorator = (view: EditorView): DecorationSet => {
  const builder = new RangeSetBuilder<Decoration>();

  // Iterate over the visible parts of the document
  for (const { from, to } of view.visibleRanges) {
    syntaxTree(view.state).iterate({
      from,
      to,
      enter: (node: SyntaxNodeRef) => {
        // We only care about JSON String nodes
        if (node.name === "String") {
          const stringValue = view.state.doc.sliceString(node.from, node.to);
          const isKeyword =
            // Match JSON Schema type keywords in quoted strings
            stringValue.match(
              /"(string|number|integer|boolean|array|object|null)"/,
            ) ||
            // Match $ref reference paths (e.g., "#/definitions/MyType" or "http://example.com/schema")
            // These should be highlighted as types
            stringValue.match(/"#\/[^"]*"/) ||
            // Also match external schema references
            stringValue.match(/"https?:\/\/[^"]*"/);

          if (isKeyword) {
            builder.add(node.from, node.to, keywordDecoration);
          }
        }

        if (node.name === "False" || node.name === "True") {
          builder.add(node.from, node.to, atomDecoration);
        }
      },
    });
  }
  return builder.finish();
};

/**
 * A class that implements PluginValue to manage the lifecycle of our decorations.
 */
class KeywordHighlighter implements PluginValue {
  decorations: DecorationSet;

  constructor(view: EditorView) {
    this.decorations = createKeywordDecorator(view);
  }

  update(update: ViewUpdate) {
    if (update.docChanged || update.viewportChanged) {
      this.decorations = createKeywordDecorator(update.view);
    }
  }

  destroy() {
    // No cleanup needed
  }
}

const keywordPlugin = ViewPlugin.fromClass(KeywordHighlighter, {
  decorations: (value: KeywordHighlighter) => value.decorations,
});

export const jsonSchemaKeywordsDecorator: Extension = [keywordPlugin];
